/**
 * Devices — the activated installations on this account.
 *
 * The important honesty here: revoking a device stops it getting a *new* license,
 * but the lease it already holds keeps working until its offline grace window closes.
 * That is the documented consequence of offline licensing, and the page says so
 * rather than implying an instant kill.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { planLabel, type Device } from '@/entities/types'
import {
  useAccount,
  useDevices,
  useReinstateDevice,
  useRemoveDevice,
  useRenameDevice,
  useRevokeDevice,
} from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  ConfirmDialog,
  Empty,
  ErrorNotice,
  Field,
  Fig,
  Loading,
  Notice,
  Panel,
  Readout,
  formatDateTime,
  formatRelative,
  platformLabel,
} from '@/shared/ui/primitives'

export function Devices() {
  const { data: account } = useAccount()
  const { data: devices, isPending, error } = useDevices()
  const revoke = useRevokeDevice()
  const rename = useRenameDevice()
  const remove = useRemoveDevice()
  const reinstate = useReinstateDevice()

  const [confirming, setConfirming] = useState<Device | null>(null)
  const [removing, setRemoving] = useState<Device | null>(null)
  const [renaming, setRenaming] = useState<Device | null>(null)
  const [draftName, setDraftName] = useState('')

  // Ephemeral rows stand for a machine token, not a laptop, and dormant ones have
  // been silent past the lease grace; the server counts neither against the cap,
  // so neither does the readout.
  const active =
    devices?.filter(
      (device) => device.status === 'active' && !device.ephemeral && !device.dormant,
    ) ?? []
  const isEnterprise = account?.plan === 'enterprise'
  const limit = isEnterprise ? null : (account?.plan === 'team' ? 3 : 2)
  const isAtLimit = limit !== null && active.length >= limit

  return (
    <AppShell>
      <PageHead
        index="03"
        eyebrow="Devices"
        title="Activated devices"
        subtitle="Each machine you sign in from consumes one activation. Reinstalling on the same machine does not: it is the same device, and it comes back as itself."
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <div className="readout-grid">
          <div>
            <Readout
              value={limit !== null ? `${active.length}/${limit}` : `${active.length}`}
              caption={isEnterprise ? 'active (your admin sets the limit, up to 10)' : 'active devices'}
              tone={isAtLimit ? 'amber' : 'signal'}
            />
          </div>
          <div>
            <Readout value={devices?.length ?? 0} caption="total ever activated" />
          </div>
          <div>
            <Readout value={planLabel(account?.plan).toUpperCase()} caption="plan" />
          </div>
        </div>

        {isAtLimit && (
          <Notice tone="warn">
            You are at your device limit ({limit}). Revoke one before signing in on another machine.
          </Notice>
        )}

        {isPending && <Loading label="Loading devices" />}
        <ErrorNotice error={error} />
        <ErrorNotice error={revoke.error} />
        <ErrorNotice error={rename.error} />
        <ErrorNotice error={remove.error} />
        <ErrorNotice error={reinstate.error} />

        <Panel title="Devices" index="01" padded={false}>
          {devices && devices.length === 0 && (
            <Empty>
              No devices yet. Run <code>chi auth login</code> in a terminal to activate one.
            </Empty>
          )}

          {devices && devices.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Platform</th>
                    <th>CLI</th>
                    <th>Last seen</th>
                    <th>Status</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={device.id}>
                      <td>
                        <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'nowrap' }}>
                          <span>{device.display_name || 'Archis CLI'}</span>
                          {device.current && <Badge tone="signal">this device</Badge>}
                          {device.ephemeral && (
                            <Link to="/machine-tokens" title="Every container presenting this token checks in as this one device">
                              <Badge tone="amber">machine token</Badge>
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="mono-note">
                        {device.ephemeral ? 'containers · CI' : platformLabel(device.platform, device.arch)}
                      </td>
                      <td>
                        <Fig>{device.cli_version || '—'}</Fig>
                      </td>
                      <td className="mono-note" title={formatDateTime(device.last_seen_at)}>
                        {formatRelative(device.last_seen_at)}
                      </td>
                      <td>
                        {device.status === 'active' && device.dormant ? (
                          <span title="Not seen for over 72 hours, so it no longer counts against your limit. It counts again when it next checks in — if there is room for it then.">
                            <Badge tone="amber">dormant</Badge>
                          </span>
                        ) : device.status === 'active' ? (
                          <Badge tone="signal" dot>
                            active
                          </Badge>
                        ) : (
                          <span
                            title={
                              device.revoked_by_admin
                                ? 'Revoked by an administrator. Only an administrator can bring it back.'
                                : 'Switched off. Reinstate it here, or just sign in again from that machine.'
                            }
                          >
                            <Badge tone="coral">
                              {device.revoked_by_admin ? 'revoked by admin' : 'revoked'}
                            </Badge>
                          </span>
                        )}
                      </td>
                      <td className="actions">
                        <div className="row" style={{ justifyContent: 'flex-end', gap: 'var(--s1)' }}>
                          {device.status === 'active' && !device.ephemeral && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setRenaming(device)
                                setDraftName(device.display_name)
                              }}
                            >
                              Rename
                            </Button>
                          )}
                          {/* A device the owner switched off comes back here. One an
                              administrator revoked does not: that is theirs to undo,
                              or the admin's control would be decorative. */}
                          {device.status !== 'active' &&
                            !device.revoked_by_admin &&
                            !device.ephemeral && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={reinstate.isPending}
                                onClick={() => reinstate.mutate(device.id)}
                              >
                                Reinstate
                              </Button>
                            )}
                          {device.status === 'active' && (
                            <Button size="sm" variant="danger" onClick={() => setConfirming(device)}>
                              Revoke
                            </Button>
                          )}
                          {/* Offered whatever state the row is in, because
                              "I have finished with this machine" is one thought
                              and should be one click. The dialog says what it
                              costs when the device is still live. */}
                          <Button size="sm" variant="danger" onClick={() => setRemoving(device)}>
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="How revocation behaves" index="02">
          <ul
            className="stack mono-note"
            style={{ ['--gap' as string]: 'var(--s2)', paddingLeft: 'var(--s5)', margin: 0 }}
          >
            <li>A revoked device is refused a new license immediately.</li>
            <li>
              The license it already holds keeps working until its offline grace window
              closes — up to 72 hours. This is the cost of licensing that works on a plane.
            </li>
            <li>Nothing on that machine is deleted. The index, graph and sessions stay.</li>
            <li>
              Signing in again from the same machine reactivates it, without consuming an
              extra activation — unless your account is at its limit by then, in which
              case something else has taken the allowance.
            </li>
            <li>
              A device an administrator revoked is the exception: only an administrator can
              bring that one back.
            </li>
            <li>
              A device that has not checked in for more than 72 hours is shown as dormant
              and stops counting against your limit. It counts again when it next checks
              in, and is refused then if the limit has filled in the meantime.
            </li>
            <li>
              Removing a device takes it off this list without deleting it. Reinstall the
              CLI on that machine and it returns here as itself, not as a second device.
            </li>
          </ul>
        </Panel>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Revoke device"
          danger
          confirmLabel="Revoke"
          pending={revoke.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() =>
            revoke.mutate(confirming.id, {
              onSuccess: () => setConfirming(null),
            })
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                <strong>{confirming.display_name || 'Archis CLI'}</strong>
                {confirming.ephemeral
                  ? ' stands for every container presenting its machine token.'
                  : ` on ${platformLabel(confirming.platform, confirming.arch)} will stop receiving license refreshes.`}
              </p>
              {confirming.ephemeral && (
                <Notice tone="warn">
                  Revoking this device revokes the machine token behind it. Every container
                  holding that token is refused its next lease, and a new token has to be
                  created and injected before they can start again.
                </Notice>
              )}
              {confirming.current && (
                <Notice tone="warn">
                  This is the device you are currently signed in from. You will need to run{' '}
                  <code>chi auth login</code> again on it.
                </Notice>
              )}
              <p className="mono-note" style={{ margin: 0 }}>
                Its cached license may keep working for up to 72 hours.
              </p>
            </>
          }
        />
      )}

      {removing && (
        <ConfirmDialog
          title="Remove device"
          danger
          confirmLabel="Remove"
          pending={remove.isPending}
          onCancel={() => setRemoving(null)}
          onConfirm={() =>
            remove.mutate(removing.id, {
              onSuccess: () => setRemoving(null),
            })
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                <strong>{removing.display_name || 'Archis CLI'}</strong> comes off this list
                and stops counting against your activations.
              </p>
              <p className="mono-note" style={{ margin: 0 }}>
                It is not deleted. If you install Archis on that machine again it returns
                here as the same device, with its history — not as a second one.
              </p>
              {removing.ephemeral && (
                <Notice tone="warn">
                  Removing this device revokes the machine token behind it. Every container
                  holding that token is refused its next lease, and a new token has to be
                  created and injected before they can start again.
                </Notice>
              )}
              {removing.current && (
                <Notice tone="warn">
                  This is the device you are currently signed in from. Its cached license
                  keeps working until the offline window closes (up to 72 hours), and you
                  will need to run <code>chi auth login</code> on it to bring it back.
                </Notice>
              )}
            </>
          }
        />
      )}

      {renaming && (
        <ConfirmDialog
          title="Rename device"
          confirmLabel="Save"
          pending={rename.isPending}
          onCancel={() => setRenaming(null)}
          onConfirm={() =>
            rename.mutate(
              { deviceId: renaming.id, displayName: draftName },
              { onSuccess: () => setRenaming(null) },
            )
          }
          body={
            <Field label="Device name" hint="Only you see this. Helps you tell machines apart.">
              <input
                className="input"
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                maxLength={80}
                autoFocus
              />
            </Field>
          }
        />
      )}
    </AppShell>
  )
}
